import { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AlertModal from "../../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../../components/customBreadcrumb/CustomBreadcrumb";
import MainLoader from "../../../../components/loading/MainLoader";
import { ISuccessState } from "../../../../interfaces/successState";
import { IRootState } from "../../../../store/configureStore";
import { TableColumn } from "react-data-table-component";
import DataTableBase from "../../../../components/dataTableBase/DataTableBase";
import SelectOption from "../../../../components/selectOption/SelectOption";
import {
  allocateAccountGroup,
  deAllocateAccountGroup,
  getCustomerAccountGroups,
  getUserAccountGroups,
} from "../../../../services/settings";
import TooltipPopup from "../../../../components/tooltipPopup/TooltipPopup";
import { AiOutlineDelete } from "react-icons/ai";
import styles from "./AssignAccountGroup.module.scss";
import SearchBar from "../../../../components/searchBar/SearchBar";
import { ICustomerAccountGroup } from "../../../../interfaces/customerAccountGroup";

const AssignAccountGroup = () => {
  const { userId, name } = useParams();
  const numericUserId = Number(userId);
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();
  const { user, loadingUserById } = useSelector(
    ({ externalUser, privileges }: IRootState) => ({
      user: privileges.dataBundleUser,
      loadingUserById: externalUser.loadingDataObject,
    })
  );
  const [groups, setGroups] = useState<ICustomerAccountGroup[]>([]);
  const [userGroups, setUserGroups] = useState<ICustomerAccountGroup[]>([]);
  const [filteredUserGroups, setFilteredUserGroups] = useState<
    ICustomerAccountGroup[]
  >([]);
  const [search, setSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [deAllocatingId, setDeallocatingId] = useState<string | undefined>();

  const getUserAccountGroupsHandler = () => {
    if (userId && user) {
      const id = Number(userId);
      setIsLoading(true);
      getUserAccountGroups(id)
        .then((r) => {
          setUserGroups(r);
          setFilteredUserGroups(r);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (userId && user) {
      setIsLoading(true);
      getCustomerAccountGroups(user.customer.id)
        .then((r) => {
          setGroups(r);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
      getUserAccountGroupsHandler();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const result = userGroups.filter((groups) => {
      return groups.accNo.toLocaleLowerCase().match(search.toLocaleLowerCase());
    });
    setFilteredUserGroups(result);
  }, [search, userGroups]);

  const deAllocateAccountGroupHandler = () => {
    if (userId && user && deAllocatingId) {
      setIsLoading(true);
      deAllocateAccountGroup(userId, deAllocatingId)
        .then(() => {
          setDeallocatingId(undefined);
          getUserAccountGroupsHandler();
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  };

  const handleSubmit = () => {
    if (numericUserId && selectedGroup) {
      console.log(selectedGroup);
      allocateAccountGroup(numericUserId, selectedGroup).then((r) => {
        if ((r.dataBundle.alreadyAssignedAccounts as string[]).length !== 0) {
          setIsSuccess({
            isSuccess: false,
            message: "This Account is already assigned",
          });
        } else {
          getUserAccountGroupsHandler();
          setIsSuccess({
            isSuccess: true,
            message: "Successfully allocated a new Account",
          });
        }
      });
    }
  };

  const columns: TableColumn<ICustomerAccountGroup>[] = [
    {
      name: "Account Number",
      selector: (row: ICustomerAccountGroup) => row?.accNo,
    },
    {
      name: "Group Nick Name",
      selector: (row: ICustomerAccountGroup) => row?.nickname,
    },
    {
      name: "Actions",
      cell: (row: ICustomerAccountGroup) => (
        <Col className="text-center">
          <TooltipPopup tooltip="Delete">
            <AiOutlineDelete
              className={styles.actionIcons}
              color="#FF6464"
              onClick={() => setDeallocatingId(row?.accNo)}
            />
          </TooltipPopup>
        </Col>
      ),
    },
  ];
  const paginationComponentOptions = {
    selectAllRowsItem: true,
  };
  return (
    <div className="page-container">
      <MainLoader show={loadingUserById || isLoading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/settings/users", title: "Manage Users" },
          { path: "/settings/users/", title: "Assign Accounts" },
        ]}
      />
      <label className="mt-2">
        Assign Accounts to <b>{name}</b>
      </label>
      <Row className="mb-3 mt-4">
        <form>
          <Row className=" mt-2">
            <Col>
              <SelectOption
                placeholder="Select Account Number"
                onChange={(val) => {
                  setSelectedGroup(val.value);
                }}
                required
                options={groups?.map((item: ICustomerAccountGroup) => ({
                  label: item.nickname
                    ? item?.accNo + " - " + item?.nickname
                    : item?.accNo,
                  value: item.accNo,
                }))}
              />
            </Col>
            <Col>
              <Button className="w-50" onClick={handleSubmit} variant="success">
                Submit
              </Button>
            </Col>
          </Row>
        </form>
      </Row>
      <Row className="justify-content-end mt-5">
        <Col className="col-md-5">
          <SearchBar placeholder="Account Number" onChange={setSearch} />
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col>
          <DataTableBase
            pagination
            paginationComponentOptions={paginationComponentOptions}
            className="mt-3 justify-content-center"
            columns={columns}
            data={filteredUserGroups ? filteredUserGroups : []}
            progressPending={false}
          />
        </Col>
      </Row>

      <AlertModal
        show={!!isSuccess}
        variant={isSuccess?.isSuccess ? "success" : "error"}
        buttons={[
          {
            text: "OK",
            onClick: () => setIsSuccess(undefined),
            variant: "success",
          },
        ]}
      >
        {isSuccess?.message}
      </AlertModal>

      <AlertModal
        show={!!deAllocatingId}
        buttons={[
          {
            variant: "danger",
            text: "OK",
            onClick: deAllocateAccountGroupHandler,
          },
          {
            variant: "success",
            text: "Cancel",
            onClick: () => {
              setDeallocatingId(undefined);
            },
          },
        ]}
        variant="error"
      >
        Are you sure you want to de-allocate this Account group?
      </AlertModal>
    </div>
  );
};

export default AssignAccountGroup;
